import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ArrowRight, Check, Loader2, Mail, MailCheck, ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/utils'

// ชุดการ์ดสำหรับหน้ายืนยันอีเมล — ใช้ร่วมกันระหว่างหน้าสมัครสมาชิก (ขั้น "ตรวจอีเมล") และหน้า /verify-email (ผลการยืนยัน)
// ให้สองหน้านี้มีรูปแบบเดียวกัน: แถบขั้นตอน → การ์ดเนื้อหา → ปุ่มดำเนินการ

const STEPS = ['สมัครสมาชิก', 'ยืนยันอีเมล', 'เริ่มใช้งาน']
const RESEND_COOLDOWN_SECONDS = 60   // รอก่อนขอส่งซ้ำ (ฝั่ง API จำกัด 5 ครั้ง/ชั่วโมงอยู่แล้ว)

const cardClass =
  'rounded-2xl border border-[#E7E5FF] bg-white p-7 shadow-[0_4px_24px_rgba(79,70,229,0.08)] sm:p-8'

const primaryButtonClass =
  'flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-accent to-accent2 text-sm font-semibold text-white shadow-lg shadow-accent/25 transition-all hover:shadow-accent/40'

/** แถบขั้นตอน — current = ลำดับขั้นที่กำลังทำ (0-2); 3 = ครบทุกขั้น */
export function VerifySteps({ current }: { current: 0 | 1 | 2 | 3 }) {
  return (
    <ol className="flex items-start" aria-label="ขั้นตอนการสมัครสมาชิก">
      {STEPS.map((label, i) => {
        const done = i < current
        const active = i === current
        return (
          <li key={label} className={cn('relative flex flex-1 flex-col items-center', i === 0 && 'pl-0')}>
            {/* เส้นเชื่อมไปขั้นก่อนหน้า */}
            {i > 0 && (
              <span
                aria-hidden
                className={cn('absolute right-1/2 top-4 h-px w-full', i <= current ? 'bg-accent' : 'bg-[#E1DCF5]')}
              />
            )}
            <span
              className={cn(
                'relative z-10 flex size-8 items-center justify-center rounded-full border-2 text-xs font-semibold',
                done && 'border-accent bg-accent text-white',
                active && 'border-accent bg-white text-accent ring-4 ring-accent/15',
                !done && !active && 'border-[#E1DCF5] bg-white text-[#9A94B8]',
              )}
            >
              {done ? <Check className="size-4" strokeWidth={3} /> : i + 1}
            </span>
            <span
              className={cn(
                'mt-2 text-center text-xs',
                done || active ? 'font-medium text-[#19172A]' : 'text-text-muted',
              )}
            >
              {label}
            </span>
          </li>
        )
      })}
    </ol>
  )
}

function IconBadge({ children, tone }: { children: React.ReactNode; tone: 'accent' | 'success' | 'danger' }) {
  const ring = {
    accent: 'bg-accent/10 text-accent',
    success: 'bg-emerald-50 text-emerald-600',
    danger: 'bg-rose-50 text-rose-600',
  }[tone]
  return <div className={cn('mx-auto flex size-16 items-center justify-center rounded-full', ring)}>{children}</div>
}

/** ขั้น "ตรวจอีเมล" หลังสมัครสำเร็จ — onResend คืนข้อความผลลัพธ์ (โยน Error ถ้าล้มเหลว) */
export function CheckEmailCard({ email, onResend }: { email: string; onResend: () => Promise<string> }) {
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS)   // เพิ่งส่งไปตอนสมัคร จึงเริ่มนับถอยหลังทันที
  const [sending, setSending] = useState(false)
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null)

  useEffect(() => {
    if (cooldown <= 0) return
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000)
    return () => clearTimeout(t)
  }, [cooldown])

  const handleResend = async () => {
    setMessage(null)
    setSending(true)
    try {
      setMessage({ text: await onResend(), ok: true })
      setCooldown(RESEND_COOLDOWN_SECONDS)
    } catch (err) {
      setMessage({ text: err instanceof Error ? err.message : 'ส่งอีเมลไม่สำเร็จ', ok: false })
    } finally {
      setSending(false)
    }
  }

  return (
    <div>
      <VerifySteps current={1} />

      <div className={cn(cardClass, 'mt-8')}>
        <IconBadge tone="accent">
          <MailCheck className="size-8" strokeWidth={1.75} />
        </IconBadge>

        <h2 className="mt-5 text-center text-xl font-semibold text-[#19172A]">ยืนยันอีเมลเพื่อเปิดใช้งานบัญชี</h2>
        <p className="mt-2 text-center text-sm text-text-muted">เราได้ส่งอีเมลยืนยันไปยังที่อยู่ด้านล่างนี้แล้ว</p>

        <div className="mt-5 flex items-center justify-center gap-2.5 rounded-lg border border-[#E7E5FF] bg-[#F7F6FF] px-4 py-3">
          <Mail className="size-4 shrink-0 text-accent" />
          <span className="break-all text-sm font-semibold text-[#19172A]">{email}</span>
        </div>

        <ol className="mt-6 space-y-3 text-sm text-[#3B3852]">
          {[
            'เปิดอีเมลที่ส่งจาก JosCoWork',
            'กดปุ่ม "ยืนยันอีเมล" ภายใน 15 นาที',
            'กลับมาเข้าสู่ระบบเพื่อเริ่มใช้งาน',
          ].map((text, i) => (
            <li key={text} className="flex items-start gap-3">
              <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-accent/10 text-[11px] font-semibold text-accent">
                {i + 1}
              </span>
              <span>{text}</span>
            </li>
          ))}
        </ol>

        <p className="mt-5 rounded-lg bg-[#FAFAFC] px-4 py-3 text-xs leading-relaxed text-text-muted">
          หากไม่พบอีเมล กรุณาตรวจสอบโฟลเดอร์จดหมายขยะ (Spam) หรือรอสักครู่แล้วลองขอส่งอีกครั้ง
        </p>

        <Link to="/login" className={cn(primaryButtonClass, 'mt-6')}>
          ไปหน้าเข้าสู่ระบบ
          <ArrowRight className="size-4" />
        </Link>

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={handleResend}
            disabled={sending || cooldown > 0}
            className="text-sm font-medium text-accent hover:underline disabled:cursor-not-allowed disabled:text-text-muted disabled:no-underline"
          >
            {sending
              ? 'กำลังส่ง...'
              : cooldown > 0
                ? `ส่งอีเมลยืนยันอีกครั้งได้ใน ${cooldown} วินาที`
                : 'ส่งอีเมลยืนยันอีกครั้ง'}
          </button>
          {message && (
            <p className={cn('mt-2 text-xs', message.ok ? 'text-text-muted' : 'text-rose-500')} role="status">
              {message.text}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export type VerifyStatus = 'loading' | 'success' | 'error'

/** ผลการยืนยันอีเมลที่หน้า /verify-email (เปิดจากลิงก์ในอีเมล) */
export function VerifyResultCard({
  status, alreadyVerified, message,
}: {
  status: VerifyStatus
  alreadyVerified?: boolean
  message?: string
}) {
  return (
    <div>
      <VerifySteps current={status === 'success' ? 3 : 1} />

      <div className={cn(cardClass, 'mt-8 text-center')}>
        {status === 'loading' && (
          <>
            <IconBadge tone="accent">
              <Loader2 className="size-8 animate-spin" />
            </IconBadge>
            <h2 className="mt-5 text-xl font-semibold text-[#19172A]">กำลังตรวจสอบลิงก์ยืนยัน</h2>
            <p className="mt-2 text-sm text-text-muted">กรุณารอสักครู่</p>
          </>
        )}

        {status === 'success' && (
          <>
            <IconBadge tone="success">
              <ShieldCheck className="size-8" strokeWidth={1.75} />
            </IconBadge>
            <h2 className="mt-5 text-xl font-semibold text-[#19172A]">
              {alreadyVerified ? 'อีเมลนี้ได้รับการยืนยันแล้ว' : 'ยืนยันอีเมลเรียบร้อยแล้ว'}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-text-muted">
              บัญชีของคุณพร้อมใช้งานแล้ว กรุณาเข้าสู่ระบบเพื่อเริ่มต้นใช้งาน JosCoWork
            </p>
            <Link to="/login" className={cn(primaryButtonClass, 'mt-6')}>
              เข้าสู่ระบบ
              <ArrowRight className="size-4" />
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <IconBadge tone="danger">
              <AlertTriangle className="size-8" strokeWidth={1.75} />
            </IconBadge>
            <h2 className="mt-5 text-xl font-semibold text-[#19172A]">ไม่สามารถยืนยันอีเมลได้</h2>
            <p className="mt-2 text-sm leading-relaxed text-text-muted">{message || 'ลิงก์ยืนยันไม่ถูกต้องหรือหมดอายุ'}</p>
            <ul className="mx-auto mt-4 max-w-xs space-y-1.5 text-left text-xs text-text-muted">
              <li>• ลิงก์ยืนยันมีอายุ 15 นาที</li>
              <li>• ลิงก์ที่ใช้แล้วหรือถูกขอใหม่ อาจใช้งานไม่ได้</li>
            </ul>
            <Link to="/login" className={cn(primaryButtonClass, 'mt-6')}>
              ไปหน้าเข้าสู่ระบบเพื่อขอลิงก์ใหม่
              <ArrowRight className="size-4" />
            </Link>
          </>
        )}
      </div>
    </div>
  )
}
